import { formatInTimeZone } from "date-fns-tz";
import {
  formatCurrency as formatLocationCurrency,
  formatPhoneNumber as formatLocationPhoneNumber,
  getCompanyTimeZone,
  getLocationConfig
} from "config/locationConfig";

const isPresent = (value) => value !== null && value !== undefined && value !== "";

const cleanText = (value) => (typeof value === "string" ? value.trim() : "");

const getUserRealName = (user) => {
  if (!user) return "";
  if (typeof user === "string") {
    const name = cleanText(user);
    return name.toLowerCase() === "user" ? "" : name;
  }

  return cleanText([user?.firstName, user?.lastName].filter(Boolean).join(" "))
    || cleanText([user?.firstname, user?.lastname].filter(Boolean).join(" "))
    || cleanText(user?.fullName)
    || cleanText(user?.displayName)
    || cleanText(user?.name)
    || "";
};

export const getUserDisplayName = (user, fallback = "User") => (
  getUserRealName(user?.userDetails)
  || getUserRealName(user?.userData)
  || getUserRealName(user?.data?.userDetails)
  || getUserRealName(user?.data?.userData)
  || getUserRealName(user?.data)
  || getUserRealName(user?.user)
  || getUserRealName(user?.authUser)
  || getUserRealName(user?.account)
  || getUserRealName(user)
  || cleanText(user?.email)
  || cleanText(user?.username)
  || cleanText(user?.userName)
  || fallback
);

const parseDate = (value) => {
  if (!isPresent(value)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatCurrency = (value, companyOrCode, options = {}) => (
  formatLocationCurrency(value, companyOrCode, {
    minimumFractionDigits: 2,
    ...options
  })
);

export const formatNumber = (value, companyOrCode, options = {}) => {
  const numericValue = Number(value ?? 0);
  if (Number.isNaN(numericValue)) return String(value ?? "");

  return new Intl.NumberFormat(getLocationConfig(companyOrCode).locale, options).format(numericValue);
};

export const formatPhoneNumber = (value, companyOrCode) => (
  formatLocationPhoneNumber(value, companyOrCode)
);

export const formatAcademyDate = (value, fallback = "-", options = {}) => {
  const date = parseDate(value);
  if (!date) return fallback;

  return new Intl.DateTimeFormat(options.locale || "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...options
  }).format(date);
};

export const formatAcademyTime = (value, fallback = "-", options = {}) => {
  const date = parseDate(value);
  if (!date) return fallback;

  return new Intl.DateTimeFormat(options.locale || "en-US", {
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
    ...options
  }).format(date);
};

export const formatAcademyDateTime = (value, fallback = "-", options = {}) => {
  const date = parseDate(value);
  if (!date) return fallback;

  return `${formatAcademyDate(date, fallback, options.dateOptions || {})}, ${formatAcademyTime(date, fallback, options.timeOptions || {})}`;
};

export const formatDateInCompanyTimeZone = (value, companyOrCode, formatString = "MMM dd, yyyy", fallback = "-") => {
  const date = parseDate(value);
  if (!date) return fallback;

  try {
    return formatInTimeZone(date, getCompanyTimeZone(companyOrCode), formatString);
  } catch (error) {
    return fallback;
  }
};

export const getDisplayTime = (time, fallback = "-") => {
  if (!time || typeof time !== "string" || time.length < 5) {
    return fallback;
  }

  const hours = parseInt(time.substr(0, 2), 10);
  if (Number.isNaN(hours)) return fallback;

  const amOrPm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  const minutes = time.substring(3, 5);
  return `${displayHours}:${minutes} ${amOrPm}`;
};
